import { LikeOutlined, DislikeOutlined, CopyOutlined } from "@ant-design/icons";
import { forwardRef, useEffect, useState } from "react";
import { motion, Variants } from "framer-motion";
import { ChatMessageFeedback, Message } from "../ChatTypes";
import "./MessageActions.scss";

interface MessageActionsProps {
  message: Message;
  isMessageComplete?: boolean; // 🎯 Específico para ESTA mensagem
  onLike: (messageId: string) => void;
  onDislike: (messageId: string) => void;
  onCopy: (text: string) => void;
}

const containerVariants: Variants = {
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
      staggerChildren: 0.1,
    },
  },
  hidden: {
    opacity: 0,
    y: 10,
    scale: 0.95,
    transition: {
      duration: 0.3,
      ease: "easeIn",
    },
  },
};

const buttonVariants: Variants = {
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" },
  },
  hidden: { opacity: 0, y: 5, scale: 0.9 },
  hover: { scale: 1.05, transition: { duration: 0.2 } },
  tap: { scale: 0.95, transition: { duration: 0.1 } },
};

// components/MessageActions/MessageActions.tsx
const MessageActions = forwardRef<HTMLDivElement, MessageActionsProps>(
  ({ message, isMessageComplete, onLike, onDislike, onCopy }, ref) => {
    const [isComplete, setIsComplete] = useState(
      message.isFromHistory || false
    );

    useEffect(() => {
      if (message.isFromHistory) {
        setIsComplete(true);
        return;
      }

      // Se recebemos a prop isMessageComplete, use ela
      if (isMessageComplete !== undefined) {
        setIsComplete(isMessageComplete);
        return;
      }

      // Fallback para a lógica anterior se a prop não for passada
      const timer = setTimeout(() => {
        setIsComplete(true);
      }, message.content.length * 15);

      return () => clearTimeout(timer);
    }, [message.content.length, message.isFromHistory, isMessageComplete]);

    return (
      <motion.div
        ref={ref}
        className="message-actions"
        variants={containerVariants}
        initial="hidden"
        animate={isComplete ? "visible" : "hidden"}
      >
        {/* ✅ Agora sim, usando as props! */}
        <motion.button
          className={`action-btn cursor-pointer ${
            message.feedback === ChatMessageFeedback.Like ? "active" : ""
          }`}
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={() => onLike(message.id)} // 🎯 Usando a prop
        >
          <LikeOutlined />
        </motion.button>

        <motion.button
          className={`action-btn cursor-pointer ${
            message.feedback === ChatMessageFeedback.Dislike ? "active" : ""
          }`}
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={() => onDislike(message.id)} // 🎯 Usando a prop
        >
          <DislikeOutlined />
        </motion.button>

        <motion.button
          className="action-btn cursor-pointer"
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={() => onCopy(message.content)} // 🎯 Usando a prop
        >
          <CopyOutlined />
        </motion.button>
      </motion.div>
    );
  }
);

MessageActions.displayName = "MessageActions";
export default MessageActions;
